import { FinanceLabFieldType } from "./FinanceLab.model";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

/**
 * Safely parse a value to a number. If the value is already a number, return it.
 * If it's a string that can be converted to a valid number, return the number.
 * Otherwise, return null.
 */
const safeParseNumber = (
    value: number | string | boolean | null | undefined,
): number | null => {
    if (value === null || value === undefined) {
        return null;
    }

    if (typeof value === "number") {
        return isNaN(value) || !isFinite(value) ? null : value;
    }

    if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed === "") {
            return null;
        }
        const parsed = Number(trimmed);

        return isNaN(parsed) || !isFinite(parsed) ? null : parsed;
    }

    return null;
};

/**
 * Format a Finance Lab metric value based on its field type.
 * Mimics the to_string() methods from core_metrics.py
 */
export const formatFinanceLabValue = (
    value: number | string | boolean | null | undefined,
    fieldType?: FinanceLabFieldType,
): string => {
    if (value === null || value === undefined) {
        return "-";
    }

    if (!fieldType) {
        return String(value);
    }

    switch (fieldType) {
        case FinanceLabFieldType.CURRENCY:
            return formatCurrency(value);

        case FinanceLabFieldType.PERCENTAGE:
            return formatPercentage(value);

        case FinanceLabFieldType.RATIO:
            return formatRatio(value);

        case FinanceLabFieldType.MULTIPLE:
            return formatMultiple(value);

        case FinanceLabFieldType.DATE:
            return formatDate(value as string);

        case FinanceLabFieldType.BOOLEAN:
            return String(value);

        case FinanceLabFieldType.INTEGER:
            return String(value);

        case FinanceLabFieldType.PERIOD:
            return String(value);

        case FinanceLabFieldType.STRING:
        default:
            return String(value);
    }
};

/**
 * Format currency: $1,234.56
 */
const formatCurrency = (
    value: number | string | boolean | null | undefined,
): string => {
    const numValue = safeParseNumber(value);
    if (numValue === null) {
        return typeof value === "string" ? value : "-";
    }
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(numValue);
};

/**
 * Format percentage: 50.00% or 1,234.56%
 * Value is expected to be decimal (0.5 -> 50%)
 */
const formatPercentage = (
    value: number | string | boolean | null | undefined,
): string => {
    const numValue = safeParseNumber(value);
    if (numValue === null) {
        return typeof value === "string" ? value : "-";
    }
    const percentValue = numValue * 100;
    const formattedNumber = percentValue.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return `${formattedNumber}%`;
};

/**
 * Format ratio: 0.50
 */
const formatRatio = (
    value: number | string | boolean | null | undefined,
): string => {
    const numValue = safeParseNumber(value);
    if (numValue === null) {
        return typeof value === "string" ? value : "-";
    }
    return numValue.toFixed(2);
};

/**
 * Format multiple: 2.30x
 */
const formatMultiple = (
    value: number | string | boolean | null | undefined,
): string => {
    const numValue = safeParseNumber(value);
    if (numValue === null) {
        return typeof value === "string" ? value : "-";
    }
    return `${numValue.toFixed(2)}x`;
};

/**
 * Format date: mm/dd/yyyy -> Dec 10, 2025
 */
const formatDate = (value: string): string => {
    if (!value || value === "-" || value === "NaT") {
        return "-";
    }

    const parsedDate = dayjs(value, "MM/DD/YYYY");

    if (!parsedDate.isValid()) {
        return "-";
    }

    return parsedDate.format("MMM D, YYYY");
};
